/*
  Copyright Epic Games, Inc. All Rights Reserved.
*/

import { getHygraphCosmosPageData, resolveOwnerSlug } from "@cosmos/hygraph-data";
import { Locale, PageDirectoryMetadataFragment } from "@cosmos/hygraph-schema";
import { getHygraphLocale, getSlugParam } from "@cosmos/next-common";
import { isError } from "@epic-mw/error-types";
import { Metadata } from "next";
import { notFound } from "next/navigation";

import { CosmosPageMetadata } from "../common/page.types";
import { checkValidParams } from "../templates/selector";

const getPageMetadata = async (
	locale: string = Locale.En,
	slug: string,
	previewParam?: string | string[],
): Promise<PageDirectoryMetadataFragment | null | undefined> => {
	if (!slug) {
		throw new Error("Missing slug");
	}

	const preview = previewParam && typeof previewParam === "string" ? previewParam : undefined;

	// Transform URL path to Hygraph slug by applying owner prefix
	const hygraphSlug = await resolveOwnerSlug(slug, preview);

	const localeArray = [locale, Locale.En] as Locale[];

	const resp = await getHygraphCosmosPageData(
		"CosmosPageDirectoryMetadata",
		{
			locale: localeArray,
			slug: hygraphSlug,
		},
		preview,
	);
	return resp?.cosmosPageDirectory;
};

export async function cosmosPageDirectoryMetadata({
	slug: rootSlug = "",
	params,
	searchParams,
	hygraphLocales,
	getAppMetadata,
}: CosmosPageMetadata): Promise<Metadata> {
	const valid = checkValidParams({ params, searchParams });
	if (!valid) {
		return notFound();
	}

	const slugParam = getSlugParam(params);
	const slug = `${rootSlug}${slugParam}`;
	const locale = params.lang;
	let pageData: PageDirectoryMetadataFragment | null | undefined;
	try {
		pageData = await getPageMetadata(getHygraphLocale(hygraphLocales, locale), slug, searchParams.preview);
	} catch (e) {
		console.error("Failed to get cosmosPageDirectoryMetadata()", { error: isError(e) && e.message });
	}
	const metaTags = pageData?.metaTags ?? {};

	return getAppMetadata(metaTags, slug, locale);
}
